/*
 * @Author:
 * @Description:
 * @Date: 2025-06-23 11:08:46
 * @LastEditors: Please set LastEditors
 * @FilePath: /rhcn-checkout-web/src/utils/tools/format.js
 */
import { i18n } from '@/language/index';
/**
 * 订阅频次标准化映射：
 * - D/1 -> per day
 * - D/7 或 W/1 -> per week
 * - D/14 或 W/2 -> every 2 weeks
 * - M/1 -> per month
 * - M/3 -> every 3 months
 * - M/6 -> every 6 months
 * - Y/1 -> per year
 */
const STANDARDIZED_FREQUENCY_MAP = {
  D_1: 'D_1',
  D_7: 'W_1',
  W_1: 'W_1',
  D_14: 'W_2',
  W_2: 'W_2',
  M_1: 'M_1',
  M_3: 'M_3',
  M_6: 'M_6',
  Y_1: 'Y_1',
};

const SUPPORTED_FREQUENCY_TYPES = ['D', 'W', 'M', 'Y'];

/**
 * 标准化频次类型，兼容历史 year/Y 输入
 */
const normalizeFrequencyType = (frequencyType) => {
  const normalizedType = String(frequencyType || '').trim().toUpperCase();
  return normalizedType === 'YEAR' ? 'Y' : normalizedType;
};

const normalizeFrequencyPoint = (frequencyPoint) => {
  const parsedPoint = Number(frequencyPoint);
  return Number.isFinite(parsedPoint) && parsedPoint > 0 ? parsedPoint : null;
};

const formatSubscriptionFrequencyByOptions = (
  frequencyType,
  frequencyPoint,
  {
    useStandardized = true,
    fallbackKey = 'subscriptionFrequencyWithoutPer',
  } = {},
) => {
  const normalizedType = normalizeFrequencyType(frequencyType);
  const normalizedPoint = normalizeFrequencyPoint(frequencyPoint);

  if (!normalizedType || !normalizedPoint) {
    return '';
  }

  if (!SUPPORTED_FREQUENCY_TYPES.includes(normalizedType)) {
    return '';
  }

  const standardizedKey = useStandardized
    ? STANDARDIZED_FREQUENCY_MAP[`${normalizedType}_${normalizedPoint}`]
    : '';

  if (standardizedKey) {
    return i18n.t(`message.checkout.subscriptionFrequencyStandardized.${standardizedKey}`);
  }

  return i18n.tc(`message.checkout.${fallbackKey}.${normalizedType}`, normalizedPoint, {
    frequencyPoint: normalizedPoint,
  });
};

/**
 * 收银台统一使用的订阅频次格式化方法。
 */
export const formatSubscriptionFrequency = (frequencyType, frequencyPoint) => {
  return formatSubscriptionFrequencyByOptions(frequencyType, frequencyPoint);
};

/**
 * @deprecated 兼容旧调用，后续统一改为 formatSubscriptionFrequency
 */
export const formatFrequencyLabel = (frequencyType, frequencyPoint) => {
  return formatSubscriptionFrequencyByOptions(frequencyType, frequencyPoint, {
    useStandardized: false,
    fallbackKey: 'subscriptionFrequency',
  });
};

/**
 * @deprecated 兼容旧调用，后续统一改为 formatSubscriptionFrequency
 */
export const getFrequencyLabel = (frequencyType, frequencyPoint) => {
  return formatSubscriptionFrequency(frequencyType, frequencyPoint);
};

/**
 * @deprecated 兼容旧调用，后续统一改为 formatSubscriptionFrequency
 */
export const getFrequencyDescription = (frequencyPoint, frequencyType) => {
  return formatSubscriptionFrequency(frequencyType, frequencyPoint);
};

/**
 * 金额格式化为千分位
 * @param {number|string} amount - 需要格式化的金额，可以是数字或字符串
 * @returns {string} - 返回格式化后的金额字符串
 * @author Nemo
 * @date 2024-06-15
 */
export const formatMoneyWithCommas = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }

  // 将金额转换为字符串并处理
  const amountStr = String(amount);

  // 分离整数部分和小数部分
  const parts = amountStr.split('.');
  const integerPart = parts[0];
  const decimalPart = parts.length > 1 ? parts[1] : '';

  // 对整数部分添加千分位分隔符
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  // 如果有小数部分，则合并整数和小数部分
  if (decimalPart) {
    return `${formattedIntegerPart}.${decimalPart}`;
  }

  return formattedIntegerPart;
};

export const dotsCommasCountries = ['PL', 'ID'];
/**
 * 金额格式化 - 句号千分位，逗号小数点格式
 * @param {number|string} amount - 需要格式化的金额，可以是数字或字符串
 * @returns {string} - 返回格式化后的金额字符串（欧洲大陆格式：千分位用句号，小数点用逗号）
 * @description 适用于欧洲大部分国家、印尼、巴西等使用句号作为千分位分隔符、逗号作为小数点分隔符的地区
 * @author Nemo
 * @date 2024-06-15
 */
export const formatAmountWithDotsCommas = (amount) => {
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }

  // 将金额转换为字符串并处理
  const amountStr = String(amount);

  // 分离整数部分和小数部分
  const parts = amountStr.split('.');
  const integerPart = parts[0];
  const decimalPart = parts.length > 1 ? parts[1] : '';

  // 对整数部分添加千分位分隔符（使用句号作为千分位分隔符）
  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  // 如果有小数部分，则使用逗号作为小数点分隔符（欧洲大陆标准）
  if (decimalPart) {
    return `${formattedIntegerPart},${decimalPart}`;
  }

  return formattedIntegerPart;
};

/**
 * 时区处理函数：将北京时间转换为当前设备时区时间
 * @param {string} beijingTimeStr - 北京时间字符串，格式：yyyy-mm-dd hh:mm:ss
 * @returns {string|null} - 返回转换为当前设备时区的时间字符串，格式：yyyy-mm-dd hh:mm:ss
 * @author Nemo
 * @date 2024-12-27
 */
export const convertBeijingTimeToLocal = (beijingTimeStr) => {
  if (!beijingTimeStr || typeof beijingTimeStr !== 'string') {
    return null;
  }

  try {
    // 将北京时间字符串格式化为ISO格式，明确指定为北京时区(UTC+8)
    const formattedTimeStr = beijingTimeStr.replace(' ', 'T') + '+08:00';

    // 创建Date对象，JavaScript会自动将其转换为用户本地时区
    const localDate = new Date(formattedTimeStr);

    // 验证日期是否有效
    if (isNaN(localDate.getTime())) {
      console.warn('时区转换失败：无效的时间格式', beijingTimeStr);
      return null;
    }

    // 格式化为 yyyy-mm-dd hh:mm:ss 格式
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const hours = String(localDate.getHours()).padStart(2, '0');
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const seconds = String(localDate.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  } catch (error) {
    console.error('时区转换异常：', error, beijingTimeStr);
    return null;
  }
};

/**
 * 计算支付按钮应该显示的金额
 * 在订阅首购场景下返回首购优惠金额，其他情况返回原金额
 * @param {Object} order - 订单对象
 * @returns {number} - 返回应该显示的金额
 * @author Nemo
 * @date 2024-12-27
 */
export const getDisplayAmount = (order) => {
  return order?.txnAmount || 0;

  // 逻辑取消，改为后端计算返回

  // 安全检查：确保order存在
  // if (!order || typeof order !== 'object') {
  //   return 0;
  // }

  // // 将字符串或数字转换为有效数字
  // const parseAmount = (value) => {
  //   if (value === null || value === undefined || value === '') {
  //     return 0;
  //   }
  //   const parsed = parseFloat(value);
  //   return isNaN(parsed) ? 0 : parsed;
  // };

  // const txnAmount = parseAmount(order.txnAmount);

  // try {
  //   // 检查是否为订阅且有subscription对象
  //   if (!order.subscription || typeof order.subscription !== 'object') {
  //     return txnAmount;
  //   }

  //   const { amountOff, orderAmount } = order.subscription;

  //   // 检查amountOff是否为有效值（不为null/undefined即可）
  //   if (amountOff === null || amountOff === undefined) {
  //     return txnAmount;
  //   }

  //   // 转换amountOff为数字
  //   const amountOffNum = parseAmount(amountOff);

  //   // 如果amountOff转换后为0或无效，说明不是首购场景
  //   if (amountOffNum === 0) {
  //     return txnAmount;
  //   }

  //   // 获取基础金额
  //   const baseAmount = parseAmount(orderAmount) || txnAmount || 0;

  //   // 计算首购金额
  //   const firstPurchaseAmount = baseAmount - amountOffNum;

  //   // 确保结果为合理的非负数
  //   if (firstPurchaseAmount < 0 || isNaN(firstPurchaseAmount)) {
  //     return txnAmount;
  //   }

  //   return firstPurchaseAmount;
  // } catch (error) {
  //   // 任何异常都返回txnAmount作为fallback
  //   console.warn('getDisplayAmount计算异常，使用fallback值：', error);
  //   return txnAmount;
  // }
};
